import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, type QueueRow } from '../api';
import { Alert, Badge, Card, EmptyState, PageHeader, Spinner } from '../components/ui';
import { IconCheckCircle } from '../components/icons';

/** The shared pool. Nobody owns a request before it is approved, so this is the
 *  same list for every approver, oldest first, since that is what ages. */
export function ApprovalQueue() {
  const queue = useQuery({ queryKey: ['queue'], queryFn: () => api.queue(), refetchInterval: 30_000 });

  if (queue.isPending) return <Spinner label="Loading the queue" />;
  if (queue.isError) {
    return <Alert>{queue.error instanceof ApiError ? queue.error.message : 'Could not load the queue.'}</Alert>;
  }

  const rows = queue.data.requests;

  return (
    <div>
      <PageHeader
        title="Approval queue"
        description="Every request awaiting approval, shared across all approvers. Oldest first."
        actions={
          rows.length > 0 ? (
            <Badge tone="warning" dot>
              {rows.length} waiting
            </Badge>
          ) : undefined
        }
      />

      {rows.length === 0 ? (
        <EmptyState
          icon={<IconCheckCircle />}
          tone="success"
          title="Nothing waiting"
          body="Every request has been dealt with. New ones appear here as soon as they are submitted."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-3xl text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
                  <th className="px-5 py-3">Reference</th>
                  <th className="px-5 py-3">Employee</th>
                  <th className="px-5 py-3">Department</th>
                  <th className="px-5 py-3">Start date</th>
                  <th className="px-5 py-3">Raised by</th>
                  <th className="px-5 py-3">Waiting</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Row key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!queue.data.canSelfApprove && rows.some((row) => row.isOwn) && (
        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          Requests you raised yourself are marked. Somebody else has to approve those.
        </p>
      )}
    </div>
  );
}

function Row({ row }: { row: QueueRow }) {
  const waiting = row.submittedAt ? ageOf(new Date(row.submittedAt)) : 'N/A';
  const stale = row.submittedAt ? Date.now() - new Date(row.submittedAt).getTime() > 48 * 3600_000 : false;

  return (
    <tr className="border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/70 dark:border-slate-800/70 dark:hover:bg-slate-800/40">
      <td className="px-5 py-3">
        <Link
          to={`/requests/${row.id}`}
          className="font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
        >
          {row.reference}
        </Link>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {row.isOwn && <Badge tone="copper">Your own request</Badge>}
          {row.reviewedBy && <Badge tone="neutral">{row.reviewedBy} is reviewing</Badge>}
        </div>
      </td>
      <td className="px-5 py-3 text-slate-900 dark:text-slate-100">{row.employeeName ?? 'N/A'}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{row.department ?? 'N/A'}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">
        {row.startDate ? new Date(row.startDate).toLocaleDateString(undefined, { dateStyle: 'medium' }) : 'N/A'}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{row.requestedBy}</td>
      <td className="px-5 py-3">
        <span className={stale ? 'font-medium text-copper-700 dark:text-copper-400' : 'text-slate-600 dark:text-slate-400'}>
          {waiting}
        </span>
      </td>
    </tr>
  );
}

function ageOf(since: Date): string {
  const hours = Math.floor((Date.now() - since.getTime()) / 3600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}
